'use client'

import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, Info, RefreshCw } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { AUTO_REFRESH_OPTIONS, formatRelativeUpdated, useAutoRefresh } from './autoRefresh'
import { StickyStackContext, useStickyStackTop } from './stickyStack'
import { usePageRefresh } from './shell/pageChrome'
import { DashboardRangePicker } from './dashboardRange'

// Shared theme tokens for a BaseDashboard and its caller-supplied content
// (toolbar controls, table cells) — so a widget/table built for one
// platform's dashboard doesn't have to recompute the same isDark ternaries
// independently to match the shell around it.
export function baseDashboardClasses(theme: ProcessedTheme) {
  const isDark = theme === 'dark'
  // Since phase 2 of the UI harmony these are the design tokens (globals.css)
  // in both themes; isDark stays for the few callers that still branch.
  return {
    isDark,
    border: 'border-border',
    panelBg: 'bg-card',
    softBg: 'bg-background',
    label: 'text-muted-foreground',
    muted: 'text-subtle',
    text: 'text-foreground',
  }
}

// "Updated Xm ago" + a configurable auto-refresh interval — originally a
// bespoke SIBS Custom Mail Alert feature (its own nowTick ticker + refresh
// button, no auto-refresh at all), hoisted here so any BaseDashboard caller
// gets both for free by passing onRefresh/lastUpdated, instead of every
// dashboard reinventing its own ticker. Renders independently of
// headerActions (SIBS still supplies its own PilotMonthStepper/TimeRangePicker/
// replay cluster there) so it shows up alongside custom header content
// rather than only replacing the plain default refresh button.
// Exported so any other page wanting the same "updated Xm ago" + persisted
// auto-refresh interval — not just a BaseDashboard — can reuse it directly
// instead of re-implementing the dropdown/ticker (see CaseEntryDetailPage's
// header, which isn't a BaseDashboard but wants identical behavior).
export function RefreshControl({
  theme,
  lastUpdated,
  onRefresh,
  refreshing,
  storageKey,
}: {
  theme: ProcessedTheme
  lastUpdated?: number | null
  onRefresh?: () => void
  refreshing?: boolean
  storageKey?: string
}) {
  const { border, muted } = baseDashboardClasses(theme)
  const { nowTick, intervalMs, setIntervalMs } = useAutoRefresh({ lastUpdated, onRefresh, storageKey })

  if (lastUpdated == null && !onRefresh) return null

  return (
    <div className="flex shrink-0 items-center gap-2">
      {lastUpdated != null && (
        <span
          title={new Date(lastUpdated).toLocaleString()}
          className={`font-mono text-label uppercase tracking-widest whitespace-nowrap ${muted}`}
        >
          updated {formatRelativeUpdated(lastUpdated, nowTick)}
        </span>
      )}
      {onRefresh && (
        <>
          <select
            value={intervalMs ?? ''}
            onChange={(e) => setIntervalMs(e.target.value ? Number(e.target.value) : null)}
            title="Auto-refresh interval"
            className={`h-7 rounded-[2px] border bg-card font-mono text-label uppercase tracking-widest px-1.5 ${border} ${muted}`}
          >
            {AUTO_REFRESH_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.ms ?? ''}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            title="Refresh"
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] border bg-card transition-colors disabled:opacity-50 hover:text-signal hover:border-signal ${border} ${muted}`}
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </>
      )}
    </div>
  )
}

// Small "(i)" button next to the title that reveals where a dashboard's data
// actually comes from — same click-toggle/outside-click-close popover
// pattern ResizableTable's own "..." MoreMenu uses, just anchored off an
// icon instead of a button. Exists because Cases now has two different data
// sources behind the same component (raw_entries_flat vs. the legacy
// /allentries join, see CasesDashboardView's DATA_SOURCE_INFO) with no other
// way for whoever's looking at the table to tell which one they're on.
function InfoButton({ theme, content }: { theme: ProcessedTheme; content: React.ReactNode }) {
  const isDark = theme === 'dark'
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Data source"
        className={`flex items-center justify-center h-5 w-5 rounded-[2px] transition-colors ${open ? 'text-signal' : 'text-subtle hover:text-signal'}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 z-30 mt-2 w-72 border border-border bg-card p-3 text-nav leading-5 text-foreground shadow-notice"
        >
          <div className="mb-1.5 font-mono text-micro uppercase tracking-widest text-subtle">
            Data source
          </div>
          {content}
        </div>
      )}
    </div>
  )
}

// A reusable page shell for "dashboard with bento widgets + table"-style
// views: header + a bento widget grid + a toolbar + a table. Extracted from
// the structure of SIBS's own Custom Mail Alert view (SibsMailView.tsx's
// CampaignsView — title row, SparkCard/BeneficiaryRankingCard bento grid,
// search toolbar, entries table) so any platform can stand up the same
// shape of dashboard without recreating that layout — plug in your own
// widgets and table, the shell only owns the shared chrome around them.
export function BaseDashboard({
  theme,
  eyebrow,
  title,
  subtitle,
  infoContent,
  onRefresh,
  refreshing,
  lastUpdated,
  storageKey,
  timeRange,
  headerActions,
  banner,
  widgets,
  widgetCols = 12,
  widgetRows = '8rem 16rem',
  widgetsOverlay,
  toolbar,
  rootRef,
  sections = false,
  children,
}: {
  theme: ProcessedTheme
  eyebrow: string
  title: string
  subtitle?: React.ReactNode
  /** Rendered inside an "(i)" popover next to the title (see InfoButton) —
   * e.g. which table/view actually backs this dashboard. Omit to hide the
   * icon entirely; most dashboards' data source is obvious enough not to
   * need this. */
  infoContent?: React.ReactNode
  onRefresh?: () => void
  refreshing?: boolean
  /** Timestamp (Date.now()) of the last successful load — renders a live
   * "updated Xm ago" label (see RefreshControl) next to the refresh
   * control/auto-refresh dropdown. Omit to hide the label; the caller owns
   * setting this (only it knows when its own fetch actually resolved). */
  lastUpdated?: number | null
  /** Namespaces the auto-refresh interval selection in localStorage (see
   * autoRefresh.ts) so it's restored on reload instead of always starting
   * back at "Auto off" — unique per dashboard (e.g. brand+view), since it's
   * a per-dashboard preference, not a global one like theme. Omit to leave
   * the interval unpersisted (resets every reload, the old behavior). */
  storageKey?: string
  /** Attached to the shell's own outer div — e.g. SIBS's Custom Mail Alert
   * view needs the real DOM node to restore list scroll position when
   * returning from a detail page (see restoreListScrollRef). Named rootRef
   * rather than ref since this is a plain prop, not React's reserved one
   * (this codebase targets React 18, which needs forwardRef for that). */
  rootRef?: React.Ref<HTMLDivElement>
  /** Extra control cluster (time range picker, replay controls, etc. — see
   * SIBS's Custom Mail Alert view) rendered before the shared
   * updated-label/auto-refresh/refresh-button cluster (RefreshControl,
   * driven by lastUpdated/onRefresh/refreshing below) rather than replacing
   * it — a dashboard with its own header controls still gets the same
   * auto-refresh every other BaseDashboard caller gets. */
  headerActions?: React.ReactNode
  /** The dashboard's date range — rendered as the shared picker, first in
   * the header. Pair with useDashboardRange (dashboardRange.ts), which
   * defaults to the last 30 days and remembers the choice in a cookie;
   * onApply may wrap its setRange to reset the caller's own state too. */
  timeRange?: { from: string; to: string; onApply: (from: string, to: string) => void; disabled?: boolean }
  /** Status/error banner rendered between the header and the widget grid
   * (e.g. a partial-data-load warning) — own its own styling/visibility. */
  banner?: React.ReactNode
  /** Bento grid content. Each widget positions itself via its own
   * col-span/col-start/row-start classes (same convention SIBS's
   * SparkCard/BeneficiaryRankingCard instances already use), so the shell
   * doesn't need to know how many widgets there are or how they're sized —
   * it just needs to know how many columns those classes were written
   * against (see widgetCols). */
  widgets?: React.ReactNode
  /** Column count for the widget grid — must match whatever col-span/
   * col-start values the widgets themselves use (SIBS's own grid is 6
   * columns; the default of 12 is what CTT's simpler 4-SparkCard row
   * divides evenly into). */
  widgetCols?: number
  /** CSS grid-template-rows for the widget grid — override if a dashboard
   * needs a different row count/heights than the default two-row layout. */
  widgetRows?: string
  /** Absolutely-positioned content layered over the widget grid (e.g. a
   * "reloading this range" progress indicator) — the grid wrapper is
   * `relative` specifically to anchor this. */
  widgetsOverlay?: React.ReactNode
  /** Search/filter/refresh row, rendered inside the shell's own bordered
   * bar above the table. Omit if the table itself owns its toolbar (e.g.
   * fused to the same card, gated behind a loading/empty state) — that
   * content just becomes part of `children` instead. */
  toolbar?: React.ReactNode
  /** The entries table (or whatever gated loading/empty/error states
   * precede it) — rendered inside the shell's own bordered, clipped card
   * (overflow-clip, not overflow-hidden: keeps a sticky <thead> able to
   * escape to the page's real scrolling ancestor — see ResizableTable.tsx
   * and SibsMailView.tsx's entries table for the full reasoning). */
  children: React.ReactNode
  /** A page made of several blocks (workers, schedules, history...) sets
   * this and wraps each block in its own DashboardSection: the children
   * are then laid out directly in the page column, one card each, instead
   * of stacked as border-b strips inside the single card above -- where
   * six blocks read as one long table with no edges between them. */
  sections?: boolean
}) {
  const { border, panelBg } = baseDashboardClasses(theme)
  // Since UI harmony phase 4 this is the design system's page (ui/PageFrame):
  // the heading is not sticky any more (the topbar is, and holds the page's
  // refresh controls), so what sticks below -- a table's toolbar and column
  // headers -- stacks under the topbar alone: the shell's StickyStackContext
  // is passed through unchanged. The `widgets` grid keeps its shape (the
  // SIBS bento, the CTT SparkCard row) under the heading, where the mock's
  // metrics strip sits.
  const stackTop = useStickyStackTop()
  usePageRefresh({ lastUpdated, onRefresh, refreshing, storageKey })

  return (
    <StickyStackContext.Provider value={stackTop}>
    <div ref={rootRef} className="mx-auto w-full max-w-[1840px] px-5 pb-10 pt-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <span className="mb-2.5 block font-mono text-label uppercase tracking-[0.1em] text-subtle">{eyebrow}</span>
          <h1 className="flex items-center gap-2 text-h1 font-normal tracking-[-0.022em] text-foreground">
            <span>{title}<span className="text-brand-magenta">.</span></span>
            {infoContent && <InfoButton theme={theme} content={infoContent} />}
          </h1>
          {subtitle && <p className="mt-2 max-w-3xl text-nav text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3 pb-1">
          {timeRange && (
            <DashboardRangePicker
              from={timeRange.from}
              to={timeRange.to}
              onApply={timeRange.onApply}
              disabled={timeRange.disabled}
              isDark={theme === 'dark'}
              border="border-border"
              muted="text-subtle"
            />
          )}
          {headerActions}
        </div>
      </div>

      {banner && <div className="mb-6">{banner}</div>}

      {widgets && (
        <div
          className="relative mb-10 grid gap-2"
          style={{ gridTemplateColumns: `repeat(${widgetCols}, minmax(0, 1fr))`, gridTemplateRows: widgetRows }}
        >
          {widgetsOverlay}
          {widgets}
        </div>
      )}

      {toolbar && (
        <div className={`mb-3 flex items-center gap-2 border px-4 py-3 ${border} ${panelBg}`}>
          {toolbar}
        </div>
      )}

      {sections ? (
        <div className="flex flex-col gap-10 [counter-reset:module]">{children}</div>
      ) : (
        <div className={`border overflow-clip ${border} ${panelBg}`}>
          {children}
        </div>
      )}
    </div>
    </StickyStackContext.Provider>
  )
}

// One block of a `sections` dashboard: a card with a titled header bar, the
// same chrome as the widget cards above it (DomainCandidateCharts' cards),
// so a page of six blocks has six visible edges. overflow-clip for the same
// reason as the single card: a sticky <thead> inside must still reach the
// page's scrolling ancestor. `padded` is off for a block that is a table
// (and its own filter bar), which wants to run edge to edge.
export function DashboardSection({
  theme,
  title,
  hint,
  actions,
  padded = true,
  className = '',
  collapsed,
  onToggle,
  children,
}: {
  theme: ProcessedTheme
  title: React.ReactNode
  hint?: React.ReactNode
  actions?: React.ReactNode
  padded?: boolean
  className?: string
  /** With onToggle the title is a chevron button and the section folds to its header row while collapsed. Controlled, so the page can open it itself (an "Add" that needs the body). */
  collapsed?: boolean
  onToggle?: () => void
  children: React.ReactNode
}) {
  void theme
  // The design system's numbered module (ui/Module.tsx): a foreground top
  // rule, "01" in the signal colour, the title at 16 px, the hint as the
  // mono summary. The page's BaseDashboard sets the counter; a section
  // outside one still renders, just unnumbered from 01.
  const folded = !!onToggle && !!collapsed
  return (
    <section className={`min-w-0 border-t border-foreground [counter-increment:module] ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {onToggle ? (
            <button type="button" onClick={onToggle} aria-expanded={!folded} className="inline-flex items-center gap-3 text-left text-title text-foreground transition-colors hover:text-signal">
              <span className="pt-1 font-mono text-label text-signal before:content-[counter(module,decimal-leading-zero)]" aria-hidden />
              <ChevronDown className={`h-[14px] w-[14px] shrink-0 text-signal transition-transform ${folded ? '-rotate-90' : ''}`} />
              <span>{title}</span>
            </button>
          ) : (
            <span className="inline-flex items-center gap-3 text-title text-foreground">
              <span className="pt-1 font-mono text-label text-signal before:content-[counter(module,decimal-leading-zero)]" aria-hidden />
              <span>{title}</span>
            </span>
          )}
          {hint && <span className="pt-[3px] font-mono text-label text-muted-foreground">{hint}</span>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1.5 pt-1">{actions}</div>}
      </div>
      {!folded && <div className={`border-t border-border ${padded ? 'px-4 py-3' : ''}`}>{children}</div>}
    </section>
  )
}
